import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';

function SuppliersList() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [setOpenDeleteDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Fetch suppliers from the "Collection Point" field in the "shipments" collection
  const fetchSuppliers = async () => {
    try {
      console.log('Fetching unique suppliers from shipments...');
      const querySnapshot = await getDocs(collection(db, 'shipments'));

      const uniqueSuppliers = new Map();

      querySnapshot.docs.forEach(doc => {
        const collectionPoint = doc.data()["Collection Point"];
        if (collectionPoint && !uniqueSuppliers.has(collectionPoint)) {
          uniqueSuppliers.set(collectionPoint, { 
            id: collectionPoint.replace(/\s+/g, '-').toLowerCase(), // Unique ID from name
            name: collectionPoint, 
            location: "N/A" // Location field placeholder, can be manually updated later
          });
        }
      });

      setSuppliers(Array.from(uniqueSuppliers.values()));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching suppliers from shipments:', err);
      setError(`Failed to fetch suppliers: ${err.message}`);
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setFormData({ name: supplier.name, location: supplier.location });
      setSelectedSupplier(supplier);
    } else {
      setFormData({ name: '', location: '' });
      setSelectedSupplier(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSupplier(null);
    setFormData({ name: '', location: '' });
  };

  const handleOpenDeleteDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setOpenDeleteDialog(true);
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (selectedSupplier) {
        await updateDoc(doc(db, 'suppliers', selectedSupplier.id), formData);
        setSuccess('Supplier updated successfully');
      } else {
        await addDoc(collection(db, 'suppliers'), formData);
        setSuccess('Supplier added successfully');
      }
      handleCloseDialog();
      fetchSuppliers();
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError('Failed to save supplier');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Loading suppliers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button component={Link} to="/dashboard" startIcon={<ArrowBackIcon />}>
                Back to Dashboard
              </Button>
              <Typography variant="h6">Suppliers List</Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add New Supplier
            </Button>
          </Box>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <TableRow key={supplier.id} hover>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.location}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton color="primary" size="small" onClick={() => handleOpenDialog(supplier)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" size="small" onClick={() => handleOpenDeleteDialog(supplier)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No suppliers found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Edit/Add Supplier Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Supplier Name" name="name" value={formData.name} onChange={handleInputChange} required sx={{ mb: 2 }} />
          <TextField fullWidth label="Location" name="location" value={formData.location} onChange={handleInputChange} required />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{selectedSupplier ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuppliersList;
